import type { Theme } from 'baseui/theme';
import { NOTIFICATION_TYPE } from '../../../../../common/constant/constant';
import type { AnnotationLabelT, AnnotationTypeT, InputFormatT, RangeSpanT, ShowNotificationT } from '../../../../../common/types/types';
import { getRandomId } from '../../../../../common/utils/utils';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT } from '../../../../types';
import { ANNOTATION_TYPE } from '../../../constants';
import { PROPAGATION_METHOD } from '../../../reducers/actions';
import type { AnnotationT, BoundingBoxT, FrameStateT } from '../../../types';
import { ACTION_TYPE, BBOX_TRACKING_WARNING_MESSAGE } from '../constants';
export function getStartFrameOfAnnotation(annotationId: string, frames: FrameStateT[]): number {
  return frames.findIndex(frame => frame.annotationStore[annotationId]);
}
export const getAnnotationSequenceAsString = (annotationSequenceRange: [number, number][]) => {
  return annotationSequenceRange.map((sequences: [number, number]) => sequences[0] !== sequences[1] ? sequences.map((frameNum: number) => frameNum + 1).join(' - ') : sequences[0] + 1).join(', ');
};

// this function loops through the frames array looking for the start and end ranges for all frame stores with annotationId
export const getAnnotationSequence = (annotationId: string, frames: Array<{
  annotationStore: {
    [annotationId: string]: unknown;
  };
}>): Map<string, RangeSpanT> => {
  const annotationSequenceMap: Map<string, RangeSpanT> = new Map<string, RangeSpanT>();
  const framesLength = frames.length;
  let i = 0; // startPointer
  let j = 0; // endPointer

  while (j < framesLength) {
    if (frames[j]?.annotationStore?.[annotationId]) {
      i = j;
      const annotationSequenceId = getRandomId();
      while (frames[j]?.annotationStore?.[annotationId]) {
        j++;
      }
      if (annotationSequenceId) annotationSequenceMap.set(annotationSequenceId, {
        start: i,
        end: j - 1
      });
    }
    j++;
  }
  return annotationSequenceMap;
};
export const getFormattedValue = (value: string, format: InputFormatT) => {
  return format === 'frames' ? (Number(value) - 1).toString() : value;
};
export const getFormattedInputValue = (value: string, format: InputFormatT) => {
  return format === 'frames' ? Number(value) + 1 : value;
};
export const getUpdatedAnnotationSequence = (newRange: RangeSpanT, annotationSequence: Map<string, RangeSpanT>) => {
  const annotationSequenceId = getRandomId();
  return {
    annotationSequenceId,
    updatedAnnotationSequence: new Map<string, RangeSpanT>(annotationSequence).set(annotationSequenceId, {
      start: newRange.start,
      end: newRange.end
    })
  };
};
export const getAnnotationSequenceArray = (annotationId: string, frames: Array<FrameStateT>, storeType?: string): [number, number][] => {
  const annotationSequenceArray: [number, number][] = [];
  const framesLength = frames.length;
  let i = 0; // startPointer
  let j = 0; // endPointer

  // Helper function to check if annotation exists in the appropriate store
  const hasAnnotation = (frame: FrameStateT) => {
    if (storeType === 'CONSENSUS_ANNOTATION_STORE') {
      return frame?.annotationConsensusStore?.[annotationId];
    } else if (storeType === 'ML_ANNOTATION_STORE') {
      return frame?.annotationMLStore?.[annotationId];
    } else {
      return frame?.annotationStore?.[annotationId];
    }
  };
  while (j < framesLength) {
    // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'FrameStateT | undefined' is not assignable to parameter of type 'FrameStateT'.
    if (hasAnnotation(frames[j])) {
      i = j;
      const annotationSequenceId = getRandomId();
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'FrameStateT | undefined' is not assignable to parameter of type 'FrameStateT'.
      while (hasAnnotation(frames[j])) {
        j++;
      }
      if (annotationSequenceId) annotationSequenceArray.push([i, j - 1]);
    }
    j++;
  }
  return annotationSequenceArray;
};
export const getIsAnnotationSequenceValid = (annotationSequence: Map<string, RangeSpanT>, maxPositions: number) => {
  const sortedAnnotationSequences = Array.from(annotationSequence.values()).sort((a, b) => a.start - b.start);
  for (let i = 0; i < sortedAnnotationSequences.length; i++) {
    const current = sortedAnnotationSequences[i];

    // check if the range is invalid
    // @ts-expect-error todo(ts-migration) TS18048 'current' is possibly 'undefined'.
    if (current.start > current.end) {
      return false;
    }

    // check if the range is out of bounds
    if (
    // @ts-expect-error todo(ts-migration) TS18048 'current' is possibly 'undefined'.
    current.start >= maxPositions ||
    // @ts-expect-error todo(ts-migration) TS18048 'current' is possibly 'undefined'.
    current.end >= maxPositions ||
    // @ts-expect-error todo(ts-migration) TS18048 'current' is possibly 'undefined'.
    current.start < 0 ||
    // @ts-expect-error todo(ts-migration) TS18048 'current' is possibly 'undefined'.
    current.end < 0) {
      return false;
    }
    const next = sortedAnnotationSequences[i + 1];
    if (next) {
      // check if the range is overlapping with the next range
      // @ts-expect-error todo(ts-migration) TS18048 'current' is possibly 'undefined'.
      if (!(current.end + 1 < next.start)) {
        return false;
      }
    }
  }
  return true;
};
export const getKeyFrameColor = (isAutomated: boolean | undefined, isLatestTracked: boolean | undefined, color: string, theme: Theme): string => {
  if (isAutomated) {
    return isLatestTracked ? theme.colors.green400 : theme.colors.gray400;
  }
  return color;
};
export const getKeyFrameBorder = (isAutomated: boolean | undefined, isLatestTracked: boolean | undefined, theme: Theme): string => {
  if (isAutomated) {
    return isLatestTracked ? `2px solid ${theme.colors.green400}` : `2px solid ${theme.colors.gray400}`;
  }
  return 'none';
};

/**
 * Calculates the left position for automated keyframes to center them
 * Automated keyframes are 60% the size of manual ones, so we need to offset them
 */
export const getAutomatedKeyFrameLeftPosition = (baseLeftPosition: number, keyFrameSize: string): string => {
  const parsedSize = parseFloat(keyFrameSize);
  const automatedSize = parsedSize * 0.6;
  const offset = (parsedSize - automatedSize) / 2;
  return `${baseLeftPosition + offset}px`;
};
export const mapBoundingBoxTrackingDataToFrameAnnotations = (boundingBoxTrackingData: BoundingBoxTrackingOutputT, startFrame: number, annotationLabel: AnnotationLabelT, showNotification?: ShowNotificationT, sourceAnnotation?: AnnotationT): {
  [key: number]: AnnotationT;
} => {
  return boundingBoxTrackingData.bboxList.reduce<{
    [key: number]: AnnotationT;
  }>((acc, bbox, index) => {
    const frameIndex = startFrame + index;
    if (!bbox) {
      showNotification?.(NOTIFICATION_TYPE.WARNING, `${BBOX_TRACKING_WARNING_MESSAGE} ${frameIndex}`);
      return acc;
    }
    acc[frameIndex] = {
      annotationType: ANNOTATION_TYPE.RECTANGLE,
      data: bbox,
      annotationLabel,
      ...(sourceAnnotation?.metadata && {
        metadata: sourceAnnotation.metadata
      })
    };
    return acc;
  }, {});
};
export const mapToTrackingInput = (bbox: BoundingBoxT): BoundingBoxTrackingInputT['bbox'] => {
  return {
    xStart: bbox.x,
    yStart: bbox.y,
    xEnd: bbox.x + bbox.width,
    yEnd: bbox.y + bbox.height
  };
};
export const getActionConfig = (annotationType: AnnotationTypeT, isBoundingBoxTrackingAvailable: boolean): Array<(typeof ACTION_TYPE)[keyof typeof ACTION_TYPE]> => {
  switch (annotationType) {
    case ANNOTATION_TYPE.RECTANGLE:
      return isBoundingBoxTrackingAvailable ? [ACTION_TYPE.TRACK, ACTION_TYPE.INTERPOLATION] : [ACTION_TYPE.INTERPOLATION];
    case ANNOTATION_TYPE.SEGMENTATION:
      return [ACTION_TYPE.TRACK_30];
    default:
      return [ACTION_TYPE.INTERPOLATION];
  }
};
export const calculateMaxFrames = ({
  isTrackingMode,
  hasInterpolation,
  hasTrack30,
  currentFrame,
  maxFrames
}: {
  isTrackingMode: boolean;
  hasInterpolation: boolean;
  hasTrack30: boolean;
  currentFrame: number;
  maxFrames: number;
}): number => {
  if (!isTrackingMode && hasInterpolation) {
    return maxFrames;
  } else if (hasTrack30) {
    return Math.min(currentFrame + 30, maxFrames);
  } else {
    return Math.min(currentFrame + 100, maxFrames);
  }
};
export const isTrackingActiveTab = (propagationMethod: (typeof PROPAGATION_METHOD)[keyof typeof PROPAGATION_METHOD] | undefined, hasTracking: boolean) => {
  // If propagation method is set, use it to determine the tab
  if (propagationMethod) {
    return propagationMethod === PROPAGATION_METHOD.TRACKING;
  }

  // Otherwise, default to tracking if available, else interpolation
  return Boolean(hasTracking);
};